import {
  resolveHasAttributedContact,
  selectServedQuestions,
  toResponderQuestionInput,
  toResponseMode,
  toThemeModeLiteral,
  toTitleFontWeightLiteral,
  type ResponderProjectInput,
} from '@/shared-responder-v2';
import { resolveIntroCopyBody } from '@/shared-responder-v2-core';
import { ProjectMode, type GetUnifiedProjectQuery } from '@/survey-graphql';

import type { SurveyQuestionSource } from './shared-responder-v2-gateway-question.mapper';

export type SurveyUnifiedProject = GetUnifiedProjectQuery['project'];

export interface SurveyResponderProjectSource {
  id: string;
  organization?: { id: string } | null;
  questions: readonly SurveyQuestionSource[];
  type?: SurveyUnifiedProject['type'];
  mode?: SurveyUnifiedProject['mode'];
  interactionType?: SurveyUnifiedProject['interactionType'];
  canJumpForward?: SurveyUnifiedProject['canJumpForward'];
  hasScreener: boolean;
  anonymous?: SurveyUnifiedProject['anonymous'];
  isAiVoiceEnabled?: SurveyUnifiedProject['isAiVoiceEnabled'];
  isUserVoiceEnabled?: SurveyUnifiedProject['isUserVoiceEnabled'];
  isCameraEnabled?: SurveyUnifiedProject['isCameraEnabled'];
  deviceTypes?: SurveyUnifiedProject['deviceTypes'];
  publicTitle?: SurveyUnifiedProject['publicTitle'];
  title?: SurveyUnifiedProject['title'];
  language?: SurveyUnifiedProject['language'];
  estimatedCompletionTime?: SurveyUnifiedProject['estimatedCompletionTime'];
  intro?: SurveyUnifiedProject['intro'];
  introCount: SurveyUnifiedProject['introCount'];
  introText?: SurveyUnifiedProject['introText'];
  isThankyouEnabled?: SurveyUnifiedProject['isThankyouEnabled'];
  thankyouText?: SurveyUnifiedProject['thankyouText'];
  thankyouDescription?: SurveyUnifiedProject['thankyouDescription'];
  themeSettings?: SurveyUnifiedProject['themeSettings'];
}

/**
 * `initiateUnifiedSession` names the server's own cursor question by id,
 * which only lands on a step the responder actually renders first if the
 * steps themselves are in `questionOrder` — the array order the query happens
 * to return is otherwise whatever the backend's storage layer chose.
 */
function toOrderedQuestions(
  questions: readonly SurveyQuestionSource[]
): readonly SurveyQuestionSource[] {
  return questions.toSorted((a, b) => a.questionOrder - b.questionOrder);
}

/**
 * Hands-free is read from `project.mode`.
 *
 * `InteractionType` is only `Text | Voice`, which distinguishes modality but
 * not moderation style, so `ProjectMode` is the only field on the live schema
 * that expresses "the AI moderates this" — the same field the legacy responder
 * reads. It is deprecated but still queryable, so this is the interim source
 * until the backend answers whether `mode` survives; nothing here invents a
 * field.
 */
function resolveIsHandsFree(mode: ProjectMode | null | undefined): boolean {
  return mode === ProjectMode.HandsFree;
}

type UnifiedProjectIntro = SurveyUnifiedProject['intro'];

function toIntroCopy(
  intro: UnifiedProjectIntro,
  legacyIntroText: string | null | undefined,
  introCount: number
): ResponderProjectInput['intro'] {
  if (intro?.isInstructionsEnabled !== true) return null;

  const subTitle = resolveIntroCopyBody({
    subTitle: intro.subTitle,
    legacyIntroText,
    introCount,
  });

  return { title: intro.title, subTitle: subTitle === '' ? null : subTitle };
}

function toIntroVideo(
  intro: UnifiedProjectIntro
): ResponderProjectInput['introVideo'] {
  if (intro?.isVideoEnabled !== true) return null;

  return intro.asset;
}

/**
 * Turns the survey project query into the schema-free input the shared session
 * builder reads. The builder preview does the same job against the manager
 * schema, and both land on this one shape — which is why the session machine
 * has no idea which of the two it is answering.
 *
 * `intro` is already audience-resolved by the backend and comes back `null`
 * when every intro row for the channel is disabled, but a present row is not
 * pre-filtered: the backend ships `asset` regardless of `isVideoEnabled` and
 * the copy regardless of `isInstructionsEnabled`, so both flags are ours to
 * honour.
 *
 * `orgFont` arrives as an argument because the query returns it as
 * `projectOrgSettings`, a sibling of `project` rather than a field on it.
 */
export function toResponderProjectInput(
  project: SurveyResponderProjectSource,
  orgFont: string | null | undefined
): ResponderProjectInput {
  return {
    id: project.id,
    orgId: project.organization?.id,
    questions: toOrderedQuestions(project.questions).map(
      (question: SurveyQuestionSource) => toResponderQuestionInput(question)
    ),
    isHandsFree: resolveIsHandsFree(project.mode),
    interactionMode: toResponseMode(project.interactionType),
    isTypelessProject: project.type == null,
    canJumpForward: project.canJumpForward,
    hasScreener: project.hasScreener,
    isAnonymous: project.anonymous,
    isAiVoiceEnabled: project.isAiVoiceEnabled,
    isUserVoiceEnabled: project.isUserVoiceEnabled,
    isCameraEnabled: project.isCameraEnabled,
    deviceTypes: project.deviceTypes,
    publicTitle: project.publicTitle,
    title: project.title,
    language: project.language,
    orgFont: orgFont ?? null,
    estimatedCompletionTime: project.estimatedCompletionTime,
    intro: toIntroCopy(
      project.intro ?? null,
      project.introText,
      project.introCount
    ),
    introVideo: toIntroVideo(project.intro ?? null),
    isThankyouEnabled: project.isThankyouEnabled,
    thankyouText: project.thankyouText,
    thankyouDescription: project.thankyouDescription,
    themeSettings:
      project.themeSettings == null
        ? null
        : {
            ...project.themeSettings,
            mode: toThemeModeLiteral(project.themeSettings.mode),
            titleFontWeight: toTitleFontWeightLiteral(
              project.themeSettings.titleFontWeight
            ),
          },
  };
}

export function toServedResponderProjectInput(
  project: SurveyResponderProjectSource,
  orgFont: string | null | undefined,
  contact: { readonly email: string | null }
): ResponderProjectInput {
  const mapped = toResponderProjectInput(project, orgFont);

  return {
    ...mapped,
    questions: selectServedQuestions(mapped.questions, {
      hasAttributedContact: resolveHasAttributedContact({
        email: contact.email,
        isAnonymousProject: mapped.isAnonymous,
      }),
    }),
  };
}
